/* global axios */
import { DataManager } from '../helper/CacheHelper/DataManager';
import ApiClient from './ApiClient';
import { openDB } from 'idb';

class CacheEnabledApiClient extends ApiClient {
  constructor(resource, options = {}) {
    super(resource, options);
    this.dataManager = new DataManager(this.accountIdFromRoute);
  }

  // eslint-disable-next-line class-methods-use-this
  get cacheModelName() {
    throw new Error('cacheModelName is not defined');
  }

  get(cache = false) {
    if (cache) {
      return this.getFromCache();
    }

    return this.getFromNetwork();
  }

  getFromNetwork() {
    return axios.get(this.url);
  }

  // eslint-disable-next-line class-methods-use-this
  extractDataFromResponse(response) {
    return response.data.payload;
  }

  // eslint-disable-next-line class-methods-use-this
  marshallData(dataToParse) {
    return { data: { payload: dataToParse } };
  }

  async getFromCache() {
    try {
      // IDB is not supported in Firefox private mode: https://bugzilla.mozilla.org/show_bug.cgi?id=781982
      await this.dataManager.initDb();
    } catch {
      return this.getFromNetwork();
    }

    const { data } = await axios.get(
      `/api/v1/accounts/${this.accountIdFromRoute}/cache_keys`
    );
    const cacheKeyFromApi = data.cache_keys[this.cacheModelName];
    const isCacheValid = await this.validateCacheKey(cacheKeyFromApi);

    let localData = [];
    if (isCacheValid) {
      localData = await this.dataManager.get({
        modelName: this.cacheModelName,
      });
    }

    if (localData.length === 0) {
      return this.refetchAndCommit(cacheKeyFromApi);
    }

    return this.marshallData(localData);
  }

  async refetchAndCommit(newKey = null) {
    const response = await this.getFromNetwork();

    try {
      await this.dataManager.initDb();

      this.dataManager.replace({
        modelName: this.cacheModelName,
        data: this.extractDataFromResponse(response),
      });

      await this.dataManager.setCacheKeys({
        [this.cacheModelName]: newKey,
      });
    } catch {
      // Ignore error
    }

    return response;
  }

  async validateCacheKey(cacheKeyFromApi) {
    if (!this.dataManager.db) {
      await this.dataManager.initDb();
    }

    const cachekey = await this.dataManager.getCacheKey(this.cacheModelName);
    return cacheKeyFromApi === cachekey;
  }

  async getOrCreatePersistentCacheWithoutDataManager({
                                                       modelName,
                                                       fetchFunction,
                                                       extractDataFromResponse = response => response.data,
                                                     }) {
    try {
      const dbName = 'custom-cache-db'; // 数据库名称
      const versionKey = `${dbName}-version`; // 用于记录版本号的键
      let dbVersion = parseInt(localStorage.getItem(versionKey), 10) || 1; // 从 localStorage 获取版本号

      // 初始化数据库
      let db = await openDB(dbName, dbVersion);

      // 动态检查并创建模型存储（如果缺失）
      if (!db.objectStoreNames.contains(modelName)) {
        dbVersion += 1; // 增加版本号
        localStorage.setItem(versionKey, dbVersion); // 更新到 localStorage
        await db.close(); // 必须先关闭当前数据库实例

        // 打开新的版本并创建缺失的模型存储
        db = await openDB(dbName, dbVersion, {
          upgrade(upgradedDb) {
            if (!upgradedDb.objectStoreNames.contains(modelName)) {
              upgradedDb.createObjectStore(modelName, {
                keyPath: 'id',
                autoIncrement: true,
              });
            }
          },
        });
      }

      // 尝试从数据库读取已缓存数据
      const cachedData = await db.getAll(modelName);
      if (cachedData.length > 0) {
        // 如果缓存数据存在，则直接返回缓存数据
        return { data: cachedData };
      }

      // 如果无缓存，则从网络获取数据并存储
      const response = await fetchFunction();
      const extractedData = extractDataFromResponse(response);

      // 存储到缓存（使用事务写入）
      const tx = db.transaction(modelName, 'readwrite');
      for (const item of extractedData) {
        await tx.store.add(item);
      }
      await tx.done;

      return { data: extractedData }; // 返回新数据
    } catch (error) {
      // 如果操作失败（如数据库无法使用），则回退到直接请求网络数据
      console.error('Error accessing cache:', error);
      const response = await fetchFunction();
      const extractedData = extractDataFromResponse(response);
      return { data: extractedData };
    }
  }

}

export default CacheEnabledApiClient;
